import React from 'react';
import { useFeatureFlag } from 'hellospa/components/feature-flags';
import { DigTheme, helloSignRebrandTheme } from 'hellospa/dig';

type FlowName =
  | 'AccountManagement'
  | 'PurchaseExperience'
  | 'FirstUseExperience'
  | 'ContinuousUse'
  | 'SignerExperience'
  | 'GlobalExperience'

/**
 * This was built as a generic so it can be used to choose between any 2 things.
 * In <Rebrand it chooses between its `fallback` and `children` props. If you're
 * replacing a whole component and you keep the same interface, this can be used
 * to choose between them.
 *
 * ```
 * const MyModal = useRebrand('SignerExperience', OldModal, NewModal);
 * return <MyModal ... />
 * ```
 */
export function useRebrand<T>(flow: FlowName, rebrand: T, fallback: T): T {
  return (useFeatureFlag(`HelloSign_MantaRays_20220113_Rebrand_${flow}`)
    ? rebrand
    : fallback
  );
}

type Props = {
  flow: FlowName
  fallback: JSX.Element
  children: JSX.Element
}
export const Rebrand: React.FC<Props> = ({ flow, fallback, children }) => {
  return useRebrand(flow, children, fallback);
};

type WrapParameters<T> = {
  flow: FlowName,
  fallback: React.ComponentType<T>
  rebrand: React.ComponentType<T>
}
export function wrapRebrandComponent<T = {}>({ flow, fallback, rebrand }: WrapParameters<T>) {
  return function HigherOrderComponent(props: T) {
    const Component = useRebrand(flow, rebrand, fallback);
    return <Component {...props} />;
  };
}

/**
 * Wraps the component in a span which applies the rebrand DIG theme.
 *
 * @author Nathan Buchar <nbuchar@dropbox.com>
 * @example
 *
 * ```
 * <RebrandTheme>
 *   <Text>Hello!</Text>
 * </RebrandTheme>
 * ```
 */
export function RebrandTheme({ children }: {
  children: React.ReactNode,
}) {
  return (
    <DigTheme theme={helloSignRebrandTheme}>
      {children}
    </DigTheme>
  );
}

/**
 * A function which wraps the component in a span which applies the rebrand DIG
 * theme.
 *
 * @author Nathan Buchar <nbuchar@dropbox.com>
 * @param {React.Component} Component - The component to wrap.
 * @example
 *
 * ```
 * const ThemedComponent = withRebrandTheme(MyComponent);
 * ```
 */
export function withRebrandTheme<T = {}>(
  Component: React.ComponentType<T>,
) {
  return function HigherOrderComponent(props: T) {
    return (
      <RebrandTheme>
        <Component {...props} />
      </RebrandTheme>
    );
  };
}
